import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types/api.types'
import { authService } from '@/services/authService'

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean

    // Actions
    setUser: (user: User | null) => void
    login: (username: string, password: string) => Promise<void>
    logout: () => void
    initAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            isAuthenticated: false,
            isLoading: true,

            setUser: (user) => set({ user, isAuthenticated: !!user }),

            login: async (username, password) => {
                try {
                    const user = await authService.login({ username, password })
                    set({ user: user, isAuthenticated: true })
                } catch (error) {
                    set({ user: null, isAuthenticated: false })
                    throw error
                }
            },

            logout: () => {
                authService.logout()
                set({ user: null, isAuthenticated: false })
            },

            initAuth: async () => {
                try {
                    if (authService.isAuthenticated()) {
                        const user = await authService.getCurrentUser()
                        set({ user, isAuthenticated: true, isLoading: false })
                    } else {
                        set({ user: null, isAuthenticated: false, isLoading: false })
                    }
                } catch (error) {
                    authService.logout()
                    set({ user: null, isAuthenticated: false, isLoading: false })
                }
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated
            }),
        }
    )
)

export const useAuth = () => {
    const { user, isAuthenticated, isLoading, setUser, login, logout, initAuth } = useAuthStore()
    return { user, isAuthenticated, isLoading, setUser, login, logout, initAuth }
}
